import { LitElement, css, html, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ref, createRef, Ref } from 'lit/directives/ref.js'

import env from 'env'

import { Config, NugsVendorVideoPlayer } from './components/players'
import { Chapter } from './components/players/chapters'
import { cacheCDNRequests } from './utils'
import { VIDEO_PLAYBACK_ERROR } from './events/types/video.events'
import { VIDEO_PLAYER_TYPE } from './constants/enums'
import { VideoPlayer } from './components/video'

import { SourceData } from './services/telemetry/types'


const ErrorMessage = () => html`
  <div class="error-message">
    <span>
      Oops! Something went wrong. Playback error.
    </span>
  </div>
`
// Responsible for css style
class WebComponent extends LitElement {
  connectedCallback() {
    super.connectedCallback()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fn = this['mounted' as keyof WebComponent] as any
    fn && fn.call(this)
  }

  constructor() {
    super()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fn = this['created' as keyof WebComponent] as any
    fn && fn.call(this)
  }
}

@customElement('nugs-video-player')
class NugsVideoPlayerComponent extends WebComponent {
  target: Ref<HTMLElement> = createRef()
  player: VideoPlayer

  @state()
  private error: boolean | null | string

  @state()
  private isFetching: boolean

  @property({ type: String, attribute: true })
  playerType: VIDEO_PLAYER_TYPE

  @property({ type: String, attribute: true })
  img?: string

  @property({ type: String, attribute: true })
  src?: string

  @property({ type: String, attribute: true })
  dash?: string

  @property({ type: String, attribute: true })
  width?: string | number

  @property({ type: String, attribute: true })
  height?: string | number

  @property({ type: String, attribute: 'application-id' })
  applicationId!: string

  @property({ type: String, attribute: 'user-id' })
  userId!: string

  @property({ type: String, attribute: 'session-id' })
  sessionId!: string

  @property({ type: String, attribute: 'receiver-id' })
  receiverId!: string

  @property({ type: Boolean, attribute: true })
  autoplay!: boolean

  @property({ type: String, attribute: true })
  sdkVersion!: string

  @property({ type: String, attribute: 'show-id' })
  showId!: string

  static styles = css`
    :host {
      width: 100%;
      max-width: 100%;
      display: block;
      position: relative;
      background-color: #000000;
      outline: none;
    }

    #loader {
      color: #fff;
      font-size:24px;
    }

    .error-message {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      width: 100%;
      padding: 8px 16px;
      box-sizing: border-box;

      background-color: #d92824;
      color: #ffffff;

      z-index: 9999;

      font-size: 12px;
    }

    .video-container {
      width: 100%;
      max-width: 100%;
    }

    video {
      outline: none;
      background-color: var(--shaka-player-background-color, black);
      display: block;
      height: var(--shaka-player-video-height, auto);
      object-fit: var(--shaka-player-object-fit, initial);
      position: relative;
      width: var(--shaka-player-video-width, 100%);

      &:focus {
        outline: none
      }
    }
  `

  constructor() {
    super()

    this.error = null
    this.playerType = this.getAttribute('player') as VIDEO_PLAYER_TYPE
    this.receiverId = this.getAttribute('receiver-id') as string
    this.userId = this.getAttribute('user-id') as string
    this.sessionId = this.getAttribute('session-id') as string
    this.applicationId = this.getAttribute('application-id') as string
    this.src = this.getAttribute('src') as string
    this.dash = this.getAttribute('dash') as string
    this.img = this.getAttribute('img') as string
    this.width = this.getAttribute('width') as string | number
    this.height = this.getAttribute('height') as string | number
    this.autoplay = (this.getAttribute('autoplay') === 'true') as boolean
    this.sdkVersion = this.getAttribute('sdk-version') as string
    this.showId = this.getAttribute('show-id') as string
    this.isFetching = true

    const config = {
      img: this.img,
      autoplay: this.autoplay,
      width: this.width,
      height: this.height,
      sdk: {
        applicationId: this.applicationId,
        version: this.sdkVersion
      },
      chromecast: {
        receiverId: this.receiverId
      },
      drm: {
        sessionId: this.sessionId
      }
    } as Config

    if (['jwplayer', 'bitmovin'].includes(this.playerType)) {
      this.createRenderRoot = () => this
    }

    this.player = new VideoPlayer(this.playerType, config, this.userId)
    this.player.src = this.dash ? { dash: this.dash, hls: this.src } : this.src
    this.player.showId = this.showId

    this.player.player.on(VIDEO_PLAYBACK_ERROR, () => {
      this.error = true
      this.requestUpdate()
    })
  }

  public async seek(time: number) {
    this.player.seek(time)
  }

  public async play() {
    this.player.play()
  }

  public async pause() {
    this.player.pause()
  }

  public async mounted() {
    cacheCDNRequests([env.NUGSNET_CDN])

    await this.player.load()

    this.isFetching = false
    this.requestUpdate()

    await this.player.mount(this.target.value)
    await this.player.init()
  }

  public set chapters(value: Chapter[]) {
    this.player.chapters = value
  }

  public set source(value: SourceData) {
    this.player.source = value
  }

  #renderBitmovinPlayer() {
    return html`
      <div class="video-container bitmovin-theme">
        <div id="video-player" ${ref(this.target)}></div>
      </div>`
  }

  #renderPlayer() {
    return html`<div ${ref(this.target)} id="video-player"></div>`
  }

  #renderLoader() {
    return html`<div id="loader">Loading...</div>`
  }

  render() {
    return html`
    ${ this.error ? ErrorMessage() : nothing }
    ${ this.isFetching ? this.#renderLoader() : nothing }
    ${ this.playerType === 'bitmovin' ? this.#renderBitmovinPlayer() :
    this.#renderPlayer() }`
  }
}


export { NugsVideoPlayerComponent }
export type { NugsVendorVideoPlayer }
